import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import AnimatedSection from '../AnimatedSection';
import {
  COMMISSION_STATUS_LABELS,
  COMMISSION_STATUS_TONES,
  REFERRAL_STATUSES,
  REFERRAL_STATUS_LABELS,
  REFERRAL_STATUS_TONES,
} from '../../constants/partnerNetwork';
import { AdminReferral, listReferrals } from '../../services/partnerNetwork';
import { formatDate, formatMoney } from '../../utils/partnerFormat';
import {
  Alert,
  EmptyState,
  Pagination,
  Spinner,
  StatusBadge,
  filterClass,
} from './ui';

/** Every referral in the network, filterable down to the ones needing a decision. */
const ReferralList: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [referrals, setReferrals] = useState<AdminReferral[]>([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 25, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const status = searchParams.get('status') || '';
  const commissionStatus = searchParams.get('commissionStatus') || '';
  const country = searchParams.get('country') || '';
  const from = searchParams.get('from') || '';
  const to = searchParams.get('to') || '';
  const page = Number(searchParams.get('page') || 1);

  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const search = searchParams.get('search') || '';

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (searchInput === search) return;
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        if (searchInput) next.set('search', searchInput);
        else next.delete('search');
        next.delete('page');
        return next;
      });
    }, 350);
    return () => window.clearTimeout(timer);
  }, [searchInput, search, setSearchParams]);

  const setFilter = (key: string, value: string | null) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (value) next.set(key, value);
      else next.delete(key);
      if (key !== 'page') next.delete('page');
      return next;
    });
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await listReferrals({
        search: search || undefined,
        status: status || undefined,
        commissionStatus: commissionStatus || undefined,
        country: country || undefined,
        from: from || undefined,
        to: to || undefined,
        page,
        limit: 25,
      });
      setReferrals(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load referrals');
    } finally {
      setLoading(false);
    }
  }, [search, status, commissionStatus, country, from, to, page]);

  useEffect(() => {
    load();
  }, [load]);

  const hasFilters = Boolean(search || status || commissionStatus || country || from || to);

  return (
    <section className="py-8 md:py-12">
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
        <AnimatedSection animation="slide-up">
          <div className="mb-8">
            <h1 className="mb-2 font-display text-2xl font-semibold text-white md:text-3xl">
              Referrals
            </h1>
            <p className="text-sm text-muted">
              Every opportunity submitted by a partner, and where it stands.
            </p>
          </div>
        </AnimatedSection>

        {error && (
          <Alert tone="error" className="mb-6">
            {error}
          </Alert>
        )}

        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-surface-border bg-surface-card p-4">
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search business, referral ID or contact…"
            className={`${filterClass} min-w-[220px] flex-1`}
          />

          <select
            value={status}
            onChange={(e) => setFilter('status', e.target.value || null)}
            className={filterClass}
            aria-label="Filter by referral status"
          >
            <option value="">All statuses</option>
            {REFERRAL_STATUSES.map((value) => (
              <option key={value} value={value}>
                {REFERRAL_STATUS_LABELS[value]}
              </option>
            ))}
          </select>

          <select
            value={commissionStatus}
            onChange={(e) => setFilter('commissionStatus', e.target.value || null)}
            className={filterClass}
            aria-label="Filter by commission status"
          >
            <option value="">Any commission</option>
            {(['pending', 'due', 'paid', 'cancelled'] as const).map((value) => (
              <option key={value} value={value}>
                {COMMISSION_STATUS_LABELS[value]}
              </option>
            ))}
          </select>

          <input
            type="text"
            value={country}
            onChange={(e) => setFilter('country', e.target.value || null)}
            placeholder="Country"
            className={`${filterClass} w-32`}
            aria-label="Filter by country"
          />

          <input
            type="date"
            value={from}
            onChange={(e) => setFilter('from', e.target.value || null)}
            className={filterClass}
            aria-label="Submitted from"
          />
          <input
            type="date"
            value={to}
            onChange={(e) => setFilter('to', e.target.value || null)}
            className={filterClass}
            aria-label="Submitted to"
          />

          {hasFilters && (
            <button
              type="button"
              onClick={() => {
                setSearchInput('');
                setSearchParams(new URLSearchParams());
              }}
              className="text-sm text-muted underline underline-offset-2 hover:text-white"
            >
              Clear
            </button>
          )}
        </div>

        <div className="rounded-xl border border-surface-border bg-surface-card">
          {loading ? (
            <div className="flex justify-center py-20">
              <Spinner className="h-10 w-10" />
            </div>
          ) : referrals.length === 0 ? (
            <div className="p-4">
              <EmptyState
                title={hasFilters ? 'No referrals match those filters' : 'No referrals yet'}
                description={
                  hasFilters
                    ? 'Try widening the search, or clear the filters.'
                    : 'Referrals appear here as soon as a partner submits one from their portal.'
                }
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1150px] text-sm">
                <thead>
                  <tr className="border-b border-surface-border text-left text-xs uppercase tracking-wide text-muted">
                    <th className="px-4 py-3 font-medium">Referral</th>
                    <th className="px-4 py-3 font-medium">Business</th>
                    <th className="px-4 py-3 font-medium">Partner</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Project value</th>
                    <th className="px-4 py-3 font-medium">Commission</th>
                    <th className="px-4 py-3 font-medium">Submitted</th>
                    <th className="px-4 py-3 text-right font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {referrals.map((referral) => (
                    <tr
                      key={referral.id}
                      className="border-b border-surface-border/60 transition-colors last:border-0 hover:bg-surface-hover/40"
                    >
                      <td className="px-4 py-3">
                        <Link
                          to={`/admin/partners/referrals/${referral.id}`}
                          className="font-mono text-xs text-primary-400 hover:underline"
                        >
                          {referral.referralId}
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        <span className="block font-medium text-white">
                          {referral.businessName}
                        </span>
                        <span className="block text-xs text-muted">
                          {[referral.businessType, referral.country]
                            .filter(Boolean)
                            .join(' · ') || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {referral.partner ? (
                          <Link
                            to={`/admin/partners/partners/${referral.partner.id}`}
                            className="text-muted hover:text-white"
                          >
                            {referral.partner.fullName}
                            <span className="block font-mono text-xs text-muted/70">
                              {referral.partner.partnerId}
                            </span>
                          </Link>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge
                          label={REFERRAL_STATUS_LABELS[referral.status]}
                          tone={REFERRAL_STATUS_TONES[referral.status]}
                        />
                      </td>
                      <td className="px-4 py-3 text-white">
                        {formatMoney(referral.projectValue)}
                        {referral.projectValue === null &&
                          referral.estimatedValue !== null && (
                            <span className="block text-xs text-muted">
                              est. {formatMoney(referral.estimatedValue)}
                            </span>
                          )}
                      </td>
                      <td className="px-4 py-3">
                        {referral.commission ? (
                          <>
                            <span className="block text-white">
                              {formatMoney(referral.commission.commissionAmount)}
                            </span>
                            <StatusBadge
                              className="mt-1"
                              label={COMMISSION_STATUS_LABELS[referral.commission.status]}
                              tone={COMMISSION_STATUS_TONES[referral.commission.status]}
                            />
                          </>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted">
                        {formatDate(referral.submittedAt)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link
                          to={`/admin/partners/referrals/${referral.id}`}
                          className="rounded-lg px-3 py-1.5 text-xs text-muted transition-colors hover:text-white"
                        >
                          Manage
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && referrals.length > 0 && (
            <div className="border-t border-surface-border px-4">
              <Pagination
                page={pagination.page}
                pages={pagination.pages}
                total={pagination.total}
                onChange={(next) => setFilter('page', String(next))}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ReferralList;
